import { DefaultValue, selector, selectorFamily } from 'recoil';
import { CommunicationLockEnum, CommunicationRoleEnum, CommunicationTypeEnum, FixedPartyEnum, LanguageIsoCodeEnum, } from '@zurich/api/party';
import { createFieldSelector } from '@zurich/common/recoil';
import { Party, PartySubType } from '../../utils/notMigratedYet';
import { localPartyClaimNoAtom, localPartyInquiryType, localPartyPolicyNoAtom, } from '../atoms';
import { localPartyAtom, localPartyPepSanctionStatusAtom, } from '../atoms/localParty';
import { communicationChannelsAtom, partyAtom } from '../atoms/party';
export const localPartySubTypeSelector = selector({
    key: 'local/party/subType',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.partySubType || null;
    },
    set: ({ get, set }, value) => {
        // no support for reset
        if (value instanceof DefaultValue || value === null)
            return;
        const atomValue = get(localPartyAtom);
        set(localPartyAtom, {
            ...atomValue,
            partySubType: value,
            // we also have to derive the partyType from subType
            partyType: PartySubType.getPartyType(value),
        }); // FIXME: we should be able to type this for the 4 cases
    },
});
export const localPartySalutationSelector = selector({
    key: 'local/party/salutation',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.salutation || null;
    },
    set: ({ get, set }, value) => {
        set(localPartyAtom, {
            ...Party.getPerson(get(localPartyAtom)),
            salutation: value instanceof DefaultValue ? null : value,
        });
    },
});
// special logic to allow frontend to show individual chips altough backend
// only suports a string of degress
// FIXME: when integrating we need to make sure that ";" is replaced by an empty string
// FIXME: this should be an array of strings in backend in order for this to work properly
export const localPartyDegreeSelectorFamily = selectorFamily({
    key: 'local/party/degreeFamily',
    get: (type) => ({ get }) => {
        const party = get(localPartyAtom);
        const degrees = party && party[type];
        return degrees ? degrees.split(';') : [];
    },
    set: (type) => ({ get, set }, value) => {
        set(localPartyAtom, {
            ...get(localPartyAtom),
            [type]: value instanceof DefaultValue
                ? []
                : value?.length > 0
                    ? value?.join(';')
                    : null,
        });
    },
});
export const localPartyAcademicDegreePrefixSelector = selector({
    key: 'local/party/academicDegreePrefix',
    get: ({ get }) => get(localPartyDegreeSelectorFamily('academicDegreePrefix')),
    set: ({ set }, value) => set(localPartyDegreeSelectorFamily('academicDegreePrefix'), value),
});
export const localPartyAcademicDegreePostfixSelector = selector({
    key: 'local/party/academicDegreePostfix',
    get: ({ get }) => get(localPartyDegreeSelectorFamily('academicDegreePostfix')),
    set: ({ set }, value) => set(localPartyDegreeSelectorFamily('academicDegreePostfix'), value),
});
/*export const localPartyDegreeAwardedSelector = selector<
  string[] //UndefinedToNull<Person['degreeAwarded']>
>({
  key: 'local/party/degreeAwarded',
  get: ({ get }) => get(localPartyDegreeSelectorFamily('degreeAwarded')),
  set: ({ set }, value) =>
    set(localPartyDegreeSelectorFamily('degreeAwarded'), value),
});*/
export const localPartyFirstNameSelector = selector({
    key: 'local/party/firstName',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.firstName || null;
    },
    set: ({ get, set }, value) => {
        set(localPartyAtom, {
            ...Party.getPerson(get(localPartyAtom)),
            firstName: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyLastNameSelector = selector({
    key: 'local/party/lastName',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.lastName || null;
    },
    set: ({ get, set }, value) => {
        set(localPartyAtom, {
            ...Party.getPerson(get(localPartyAtom)),
            lastName: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyNameAffixSelector = selector({
    key: 'local/party/nameAffix',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.nameAffix || null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getPerson(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            nameAffix: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyPlaceOfBirthSelector = selector({
    key: 'local/party/placeOfBirth',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.placeOfBirth || null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getPerson(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            placeOfBirth: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyCountryOfBirthSelector = selector({
    key: 'local/party/countryOfBirth',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.countryOfBirth || null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getPerson(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            countryOfBirth: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyCivilStatusSelector = selector({
    key: 'local/party/civilStatus',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.civilStatus || null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getPerson(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            civilStatus: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyOrganizationNameSelector = selector({
    key: 'local/party/organizationName',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return Party.isLegalEntity(party)
            ? (party?.organizationName ?? null)
            : null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getLegalEntity(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            organizationName: value instanceof DefaultValue ? '' : (value ?? ''),
        });
    },
});
export const localPartyBusinessNameSelector = selector({
    key: 'local/party/businessName',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.businessName || null;
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party.businessData,
                businessName: value instanceof DefaultValue || value === null ? undefined : value,
            },
        });
    },
});
export const localPartyDateOfBirthSelector = selector({
    key: 'local/party/dateOfBirth',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.dateOfBirth || null;
    },
    set: ({ get, set }, value) => {
        set(localPartyAtom, {
            ...Party.getPerson(get(localPartyAtom)),
            dateOfBirth: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyPersonDataFileUploadSelector = selector({
    key: 'local/party/personDatafileUpload',
    get: ({ get }) => {
        const party = get(partyAtom);
        const firstName = get(localPartyFirstNameSelector);
        const lastName = get(localPartyLastNameSelector);
        const birthDate = get(localPartyDateOfBirthSelector);
        return (firstName !== party.firstName ||
            lastName !== party.lastName ||
            birthDate !== party.dateOfBirth);
    },
});
export const localPartyInquirySubjectSelector = selector({
    key: 'local/party/inquirySubjectSelector',
    get: ({ get }) => {
        const inquiryType = get(localPartyInquiryType);
        return inquiryType === 'policy'
            ? get(localPartyPolicyNoAtom)
            : get(localPartyClaimNoAtom);
    },
});
export const localPartyDateOfDeathSelector = selector({
    key: 'local/party/dateOfDeath',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.dateOfDeath || null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getPerson(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            dateOfDeath: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyCitizenshipsSelector = selector({
    key: 'local/party/citizenships',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.citizenships || [];
    },
    set: ({ get, set }, value) => {
        set(localPartyAtom, {
            ...Party.getPerson(get(localPartyAtom)),
            citizenships: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyMarriageOnSelector = selector({
    key: 'local/party/marriageOn',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.marriageOn || null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getPerson(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            marriageOn: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyChildrenSelector = selector({
    key: 'local/party/children',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.children || null;
    },
    set: ({ get, set }, value) => {
        set(localPartyAtom, {
            ...Party.getPerson(get(localPartyAtom)),
            children: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyOccupationSelector = selector({
    key: 'local/party/occupation',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.occupation || null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getPerson(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            occupation: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyProfessionSelector = selector({
    key: 'local/party/profession',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.profession || null;
    },
    set: ({ get, set }, value) => {
        set(localPartyAtom, {
            ...Party.getPerson(get(localPartyAtom)),
            profession: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyLegalFormSelector = selector({
    key: 'local/party/legalForm',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.legalForm || null;
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party?.businessData,
                legalForm: value instanceof DefaultValue ? null : value,
            },
        });
    },
});
export const localPartyECommunicationSelector = selector({
    key: 'local/party/eCommunication',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return typeof party?.eCommunication === 'boolean'
            ? party?.eCommunication
            : null;
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            eCommunication: typeof value === 'boolean' ? value : null,
        });
    },
});
export const localPartyPreferredLanguageSelector = selector({
    key: 'local/party/preferredLanguage',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.preferredLanguage || null;
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            preferredLanguage: value instanceof DefaultValue
                ? LanguageIsoCodeEnum.DEU
                : value,
        });
    },
});
export const localPartyCommunicationLockSelector = selector({
    key: 'local/party/communicationLock',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.communicationLock || CommunicationLockEnum.UNB;
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            communicationLock: value instanceof DefaultValue
                ? CommunicationLockEnum.UNB
                : value,
        });
    },
});
export const localPartyBusinessSectorsSelector = selector({
    key: 'local/party/businessSectors',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.businessSectors || [];
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party?.businessData,
                businessSectors: value instanceof DefaultValue ? [] : value,
            },
        });
    },
});
export const localPartyOwnershipCheckSelector = selector({
    key: 'local/party/ownershipCheck',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.ownershipCheck || false;
    },
    set: ({ get, set }, value) => {
        set(localPartyAtom, {
            ...get(localPartyAtom),
            ownershipCheck: value instanceof DefaultValue ? false : value,
        });
    },
});
export const localPartyPubliclyTradedSelector = selector({
    key: 'local/party/publiclyTraded',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.publiclyTraded || false;
    },
    set: ({ get, set }, value) => {
        const party = Party.getLegalEntity(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            publiclyTraded: value instanceof DefaultValue ? false : value,
        });
    },
});
export const localPartyMajorCustomerSelector = selector({
    key: 'local/party/majorCustomer',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.majorCustomer || false;
    },
    set: ({ get, set }, value) => {
        const party = Party.getLegalEntity(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            majorCustomer: value instanceof DefaultValue ? false : value,
        });
    },
});
export const localPartyClubObjectiveSelector = selector({
    key: 'local/party/clubObjective',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.clubObjective || null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getLegalEntity(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party.businessData,
                clubObjective: value instanceof DefaultValue ? null : value,
            },
        });
    },
});
export const localPartyOrganizationTypeSelector = selector({
    key: 'local/party/organizationType',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.organizationType || null;
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party.businessData,
                organizationType: value instanceof DefaultValue || value === null ? undefined : value,
            },
        });
    },
});
export const localPartyUpdateEUToPrivateSelector = selector({
    key: 'local/party/updateEuToPrivate',
    get: ({ get }) => {
        const { businessData, extendedPartyData: { communicationChannels = [] } = {}, } = get(localPartyAtom);
        return {
            businessData,
            communicationChannels,
        };
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        if (value instanceof DefaultValue) {
            // Shouldnt get here
            return;
        }
        set(localPartyAtom, {
            ...party,
            businessData: value.businessData,
            extendedPartyData: {
                ...party.extendedPartyData,
                ...(value.communicationChannels && {
                    communicationChannels: value.communicationChannels,
                }),
            },
        });
        if (value.communicationChannels) {
            set(communicationChannelsAtom, {
                partyNo: party.partyNo,
                channels: value.communicationChannels,
            });
        }
    },
});
export const localPartyUpdateVereinSelector = selector({
    key: 'local/party/updateVerein',
    get: ({ get }) => {
        const { businessData } = get(localPartyAtom);
        return {
            numberOfClubMembers: businessData?.numberOfClubMembers,
            zvrNumber: businessData?.zvrNumber,
            clubObjective: businessData?.clubObjective,
        };
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        if (value instanceof DefaultValue) {
            // Shouldnt get here
            return;
        }
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party.businessData,
                ...value,
            },
        });
    },
});
export const localPartyUpdateUnternehmenSelector = selector({
    key: 'local/party/updateUnternehmen',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return {
            ownershipCheck: party.ownershipCheck,
            publiclyTraded: party.publiclyTraded,
            majorCustomer: party.majorCustomer,
        };
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        if (value instanceof DefaultValue) {
            // Shouldnt get here
            return;
        }
        set(localPartyAtom, {
            ...party,
            ...value,
        });
    },
});
// Access to extendedPartyData.communicationChannels array
export const localPartyCommunicationChannelsSelector = selector({
    key: 'local/party/communicationChannels',
    get: ({ get }) => {
        const extendedPartyData = get(localPartyExtenedDataSelector);
        return extendedPartyData?.communicationChannels || [];
    },
    set: ({ get, set }, value) => {
        const extendedPartyData = get(localPartyExtenedDataSelector);
        set(localPartyExtenedDataSelector, {
            ...extendedPartyData,
            communicationChannels: value instanceof DefaultValue || value === null ? [] : value,
        });
    },
});
// access a specific comm channel in extendedPartyData.communicationChannels array
// selected by channelType and role
export const localPartyCommunicationChannelSelectorFamily = selectorFamily({
    key: 'local/party/communicationChannel',
    get: ({ communicationChannelType, communicationRole, index = 0 }) => ({ get }) => {
        const communicationChannels = get(localPartyCommunicationChannelsSelector);
        const channelsFiltered = communicationChannels?.filter((c) => c.communicationChannelType === communicationChannelType &&
            c.communicationRole === communicationRole);
        return channelsFiltered ? channelsFiltered[index] : undefined;
    },
    set: ({ communicationChannelType, communicationRole, index = 0 }) => ({ get, set }, value) => {
        const communicationChannels = get(localPartyCommunicationChannelsSelector);
        const foundEntries = [];
        // Filter out the found entries and set array as the remaining entries
        const otherEntries = communicationChannels?.reduce((noMatchEntries, c) => {
            if (c.communicationChannelType === communicationChannelType &&
                c.communicationRole === communicationRole) {
                foundEntries.push(c);
            }
            else {
                noMatchEntries.push(c);
            }
            return noMatchEntries;
        }, []);
        if (value instanceof DefaultValue || value === undefined) {
            throw new Error('localPartyCommunicationChannelSelectorFamily requires a valid value');
        }
        // we add type and role so we can get it with these attributes again
        const fullValue = value
            ? {
                ...value,
                communicationChannelType,
                communicationRole,
            }
            : null;
        // FIXME: we don't know the types of the array elements - refactor this to be more typesafe
        // Channel(s) with role already exist(s) and we want to edit a specific/first one
        if (foundEntries.length > 0) {
            foundEntries[index] = fullValue;
        }
        else {
            // We add a new channel with this role
            foundEntries[0] = fullValue;
        }
        set(localPartyCommunicationChannelsSelector, [
            ...(otherEntries || []),
            ...foundEntries,
        ]);
    },
});
// helper to avoid setting type and casting for every geo entry
export const localPartyGeoAdressSelectorFamily = selectorFamily({
    key: 'local/party/communicationChannelArray/geoAdress',
    get: (communicationRole) => ({ get }) => {
        const address = get(localPartyCommunicationChannelSelectorFamily({
            communicationChannelType: 'GEO',
            communicationRole,
        }));
        return address || null;
    },
    set: (communicationRole) => ({ set }, value) => {
        const selector = localPartyCommunicationChannelSelectorFamily({
            communicationChannelType: 'GEO',
            communicationRole,
        });
        // FIXME: we cannot just cast this
        set(selector, value);
    },
});
export const localPartyCountrySelectorFamily = selectorFamily({
    key: 'local/party/communicationChannelArray/country',
    get: (communicationRole) => ({ get }) => get(localPartyGeoAdressSelectorFamily(communicationRole))?.country ||
        null,
    set: (communicationRole) => ({ get, set }, value) => {
        const selector = localPartyGeoAdressSelectorFamily(communicationRole);
        const address = get(selector); // FIXME: we cast here becouse we don't have all values
        set(selector, {
            ...address,
            country: (value instanceof DefaultValue
                ? ''
                : value),
        });
    },
});
export const localPartyPostalCodeSelectorFamily = selectorFamily({
    key: 'local/party/communicationChannelArray/postalCode',
    get: (communicationRole) => ({ get }) => get(localPartyGeoAdressSelectorFamily(communicationRole))?.postalCode ||
        null,
    set: (communicationRole) => ({ get, set }, value) => {
        const selector = localPartyGeoAdressSelectorFamily(communicationRole);
        const address = get(selector); // FIXME: we cast here becouse we don't have all values
        set(selector, {
            ...address,
            postalCode: value instanceof DefaultValue || value === null ? '' : value,
        });
    },
});
export const localPartyCitySelectorFamily = selectorFamily({
    key: 'local/party/communicationChannelArray/city',
    get: (communicationRole) => ({ get }) => get(localPartyGeoAdressSelectorFamily(communicationRole))?.city || null,
    set: (communicationRole) => ({ get, set }, value) => {
        const selector = localPartyGeoAdressSelectorFamily(communicationRole);
        const address = get(selector); // FIXME: we cast here becouse we don't have all values
        set(selector, {
            ...address,
            city: value instanceof DefaultValue || value === null ? '' : value,
        });
    },
});
export const localPartyStreetSelectorFamily = selectorFamily({
    key: 'local/party/communicationChannelArray/street',
    get: (communicationRole) => ({ get }) => get(localPartyGeoAdressSelectorFamily(communicationRole))?.street || null,
    set: (communicationRole) => ({ get, set }, value) => {
        const selector = localPartyGeoAdressSelectorFamily(communicationRole);
        const address = get(selector); // FIXME: we cast here becouse we don't have all values
        set(selector, {
            ...address,
            street: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyHouseNumberSelectorFamily = selectorFamily({
    key: 'local/party/communicationChannelArray/houseNumber',
    get: (communicationRole) => ({ get }) => get(localPartyGeoAdressSelectorFamily(communicationRole))?.houseNumber ||
        null,
    set: (communicationRole) => ({ get, set }, value) => {
        const selector = localPartyGeoAdressSelectorFamily(communicationRole);
        const address = get(selector); // FIXME: we cast here becouse we don't have all values
        set(selector, {
            ...address,
            houseNumber: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
// function is used in localPartyCommunicationChannel
export function setHousenumberFrom(val, houseNumberParts) {
    return val instanceof DefaultValue || val?.length === 0
        ? null
        : houseNumberParts && houseNumberParts.length > 1
            ? `${val}-${houseNumberParts.pop()}`
            : val;
}
export const localPartyHouseNumberFromSelectorFamily = selectorFamily({
    key: 'local/party/communicationChannelArray/houseNumberFrom',
    get: (communicationRole) => ({ get }) => {
        const houseNumber = get(localPartyHouseNumberSelectorFamily(communicationRole));
        return houseNumber?.split('-').shift() || null;
    },
    set: (communicationRole) => ({ get, set }, value) => {
        const houseNumber = get(localPartyHouseNumberSelectorFamily(communicationRole));
        const houseNumberParts = houseNumber &&
            houseNumber.split('-').filter((element) => element !== '');
        set(localPartyHouseNumberSelectorFamily(communicationRole), setHousenumberFrom(value, houseNumberParts));
    },
});
// function is used in localPartyCommunicationChannel
export function setHousenumberToMoreParts(val, houseNumber, houseNumberParts) {
    if (val instanceof DefaultValue) {
        return houseNumber;
    }
    if (houseNumberParts && houseNumberParts.length > 1) {
        if (val?.length !== 0) {
            return `${houseNumberParts.shift()}-${val}`;
        }
        // we essentially want to delete the houseNumberTo part
        return `${houseNumberParts[0]}`;
    }
    return houseNumber;
}
export function setHousenumberToOnePart(val, houseNumber) {
    return val instanceof DefaultValue || val?.length === 0
        ? houseNumber
        : `${houseNumber}-${val}`;
}
export const localPartyHouseNumberToSelectorFamily = selectorFamily({
    key: 'local/party/communicationChannelArray/houseNumberTo',
    get: (communicationRole) => ({ get }) => {
        const houseNumber = get(localPartyHouseNumberSelectorFamily(communicationRole));
        const houseNumberParts = houseNumber?.split('-');
        return houseNumberParts && houseNumberParts.length > 1
            ? houseNumberParts.pop() || null
            : null;
    },
    set: (communicationRole) => ({ get, set }, value) => {
        const houseNumber = get(localPartyHouseNumberSelectorFamily(communicationRole));
        const houseNumberParts = houseNumber && houseNumber.split('-');
        if (houseNumberParts && houseNumberParts.length > 1) {
            set(localPartyHouseNumberSelectorFamily(communicationRole), setHousenumberToMoreParts(value, houseNumber, houseNumberParts));
        }
        else {
            set(localPartyHouseNumberSelectorFamily(communicationRole), setHousenumberToOnePart(value, houseNumber));
        }
    },
});
export const localPartyStaircaseSelectorFamily = selectorFamily({
    key: 'local/party/communicationChannelArray/staircase',
    get: (communicationRole) => ({ get }) => get(localPartyGeoAdressSelectorFamily(communicationRole))?.staircase ||
        null,
    set: (communicationRole) => ({ get, set }, value) => {
        const selector = localPartyGeoAdressSelectorFamily(communicationRole);
        const address = get(selector); // FIXME: we cast here becouse we don't have all values
        set(selector, {
            ...address,
            staircase: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyDoorNumberSelectorFamily = selectorFamily({
    key: 'local/party/communicationChannelArray/doorNumber',
    get: (communicationRole) => ({ get }) => get(localPartyGeoAdressSelectorFamily(communicationRole))?.doorNumber ||
        null,
    set: (communicationRole) => ({ get, set }, value) => {
        const selector = localPartyGeoAdressSelectorFamily(communicationRole);
        const address = get(selector); // FIXME: we cast here becouse we don't have all values
        set(selector, {
            ...address,
            doorNumber: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyDeliveryNoticeSelectorFamily = selectorFamily({
    key: 'local/party/communicationChannelArray/deliveryNotice',
    get: (communicationRole) => ({ get }) => get(localPartyGeoAdressSelectorFamily(communicationRole))
        ?.deliveryNotice || null,
    set: (communicationRole) => ({ get, set }, value) => {
        const selector = localPartyGeoAdressSelectorFamily(communicationRole);
        const address = get(selector); // FIXME: we cast here becouse we don't have all values
        set(selector, {
            ...address,
            deliveryNotice: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyFoundingYearSelector = selector({
    key: 'local/party/foundingYear',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.foundingYear || null;
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party.businessData,
                foundingYear: value instanceof DefaultValue || value === null ? undefined : value,
            },
        });
    },
});
export const localPartyLiquidationDateSelector = selector({
    key: 'local/party/liquidationDate',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.liquidationDate || null;
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party.businessData,
                liquidationDate: value instanceof DefaultValue || value === null ? undefined : value,
            },
        });
    },
});
export const localPartyNumberOfEmployeesSelector = selector({
    key: 'local/party/numberOfEmployees',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.numberOfEmployees || null;
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party.businessData,
                numberOfEmployees: value instanceof DefaultValue || value === null ? undefined : value,
            },
        });
    },
});
export const localPartyNumberOfClubMembersSelector = selector({
    key: 'local/party/numberOfClubMembers',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.numberOfClubMembers || null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getLegalEntity(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party.businessData,
                numberOfClubMembers: value instanceof DefaultValue ? null : value,
            },
        });
    },
});
export const localPartyCommercialRegisterNumberSelector = selector({
    key: 'local/party/commercialRegisterNumber',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.commercialRegisterNumber || null;
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party.businessData,
                commercialRegisterNumber: value instanceof DefaultValue || value === null ? undefined : value,
            },
        });
    },
});
export const localPartyGisaNumberSelector = selector({
    key: 'local/party/gisaNumber',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.gisaNumber || null;
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party.businessData,
                gisaNumber: value instanceof DefaultValue || value === null ? undefined : value,
            },
        });
    },
});
export const localPartyUidSelector = selector({
    key: 'local/party/uid',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.uid || null;
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party.businessData,
                uid: value instanceof DefaultValue || value === null ? undefined : value,
            },
        });
    },
});
export const localPartyDunsNumberSelector = selector({
    key: 'local/party/dunsNumber',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.dunsNumber || null;
    },
    set: ({ get, set }, value) => {
        const party = get(localPartyAtom);
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party.businessData,
                dunsNumber: value instanceof DefaultValue || value === null ? undefined : value,
            },
        });
    },
});
export const localPartyExtenedDataSelector = selector({
    key: 'local/party/extendedPartyData',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.extendedPartyData || null;
    },
    set: ({ get, set }, value) => {
        set(localPartyAtom, {
            ...get(localPartyAtom),
            extendedPartyData: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyInsolvencySelector = selector({
    key: 'local/party/insolvency',
    get: ({ get }) => {
        const extendedPartyData = get(localPartyExtenedDataSelector);
        return extendedPartyData?.insolvency || null;
    },
    set: ({ get, set }, value) => {
        const selector = localPartyExtenedDataSelector;
        set(selector, {
            ...get(selector),
            insolvency: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
// TODO: move to speperate atom and file!
export const localPartyInsolvencyStartSelector = selector({
    key: 'local/party/insolvencyStart',
    get: ({ get }) => {
        const insolvency = get(localPartyInsolvencySelector);
        return insolvency?.insolvencyStart || null;
    },
    set: ({ get, set }, value) => {
        const selector = localPartyInsolvencySelector;
        set(selector, {
            ...get(selector),
            insolvencyStart: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyInsolvencyEndSelector = selector({
    key: 'local/party/insolvencyEnd',
    get: ({ get }) => {
        const insolvency = get(localPartyInsolvencySelector);
        return insolvency?.insolvencyEnd || null;
    },
    set: ({ get, set }, value) => {
        const selector = localPartyInsolvencySelector;
        set(selector, {
            ...get(selector),
            insolvencyEnd: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyDiplomaticDataSelector = selector({
    key: 'local/party/diplomaticData',
    get: ({ get }) => {
        const extendedPartyData = get(localPartyExtenedDataSelector);
        return extendedPartyData?.diplomaticData || {};
    },
    set: ({ get, set }, value) => {
        set(localPartyExtenedDataSelector, {
            ...get(localPartyExtenedDataSelector),
            diplomaticData: value instanceof DefaultValue ? undefined : value,
        });
    },
});
export const localPartyDiplomaticStatusSelector = selector({
    key: 'local/party/diplomaticStatus',
    get: ({ get }) => {
        const diplomaticData = get(localPartyDiplomaticDataSelector);
        return diplomaticData?.diplomaticStatus ?? null;
    },
    set: ({ get, set }, value) => {
        const diplomaticData = get(localPartyDiplomaticDataSelector);
        set(localPartyDiplomaticDataSelector, {
            ...diplomaticData,
            diplomaticStatus: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyDDocNumberSelector = selector({
    key: 'local/party/dDocNumber',
    get: ({ get }) => {
        const diplomaticData = get(localPartyDiplomaticDataSelector);
        return diplomaticData?.dDocNumber || null;
    },
    set: ({ get, set }, value) => {
        const diplomaticData = get(localPartyDiplomaticDataSelector);
        set(localPartyDiplomaticDataSelector, {
            ...diplomaticData,
            dDocNumber: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyDDocSinceSelector = selector({
    key: 'local/party/dDocSince',
    get: ({ get }) => {
        const diplomaticData = get(localPartyDiplomaticDataSelector);
        return diplomaticData?.dDocSince || null;
    },
    set: ({ get, set }, value) => {
        const diplomaticData = get(localPartyDiplomaticDataSelector);
        set(localPartyDiplomaticDataSelector, {
            ...diplomaticData,
            dDocSince: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyDDocUntilSelector = selector({
    key: 'local/party/dDocUntil',
    get: ({ get }) => {
        const diplomaticData = get(localPartyDiplomaticDataSelector);
        return diplomaticData?.dDocUntil || null;
    },
    set: ({ get, set }, value) => {
        const diplomaticData = get(localPartyDiplomaticDataSelector);
        set(localPartyDiplomaticDataSelector, {
            ...diplomaticData,
            dDocUntil: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyZvrNumberSelector = selector({
    key: 'local/party/zvrNumber',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.businessData?.zvrNumber || null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getLegalEntity(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            businessData: {
                ...party.businessData,
                zvrNumber: value instanceof DefaultValue ? null : value,
            },
        });
    },
});
export const localPartyFixedPartySelector = selector({
    key: 'local/party/fixedParty',
    get: ({ get }) => {
        const extendedPartyData = get(localPartyExtenedDataSelector);
        return extendedPartyData?.fixedParty?.fixedParty || null;
    },
    set: ({ get, set }, value) => {
        const selector = localPartyExtenedDataSelector;
        const extendedPartyData = get(selector);
        set(selector, {
            ...extendedPartyData,
            fixedParty: {
                ...extendedPartyData?.fixedParty,
                fixedParty: value instanceof DefaultValue || value === null
                    ? FixedPartyEnum.KEIN
                    : value,
            },
        });
    },
});
export const localPartyVvoNumberSelector = selector({
    key: 'local/party/vvoNumber',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.extendedPartyData?.fixedParty?.vvoNumber || null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getLegalEntity(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            extendedPartyData: {
                ...party.extendedPartyData,
                fixedParty: {
                    ...party.extendedPartyData?.fixedParty,
                    vvoNumber: value instanceof DefaultValue ? null : value,
                },
            },
        });
    },
});
export const localPartyEducationSelector = selector({
    key: 'local/party/education',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.education || null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getPerson(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            education: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyEmploymentSelector = selector({
    key: 'local/party/employment',
    get: ({ get }) => {
        const party = get(localPartyAtom);
        return party?.employment || null;
    },
    set: ({ get, set }, value) => {
        const party = Party.getPerson(get(localPartyAtom));
        set(localPartyAtom, {
            ...party,
            employment: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyEmailSelector = selector({
    key: 'local/party/communicationChannelArray/emailAddress',
    get: ({ get }) => {
        const communicationChannel = get(localPartyCommunicationChannelSelectorFamily({
            communicationChannelType: CommunicationTypeEnum.EMAIL,
            communicationRole: CommunicationRoleEnum.EH,
        }));
        return communicationChannel?.emailAddress || null;
    },
    set: ({ get, set }, value) => {
        const selector = localPartyCommunicationChannelSelectorFamily({
            communicationChannelType: CommunicationTypeEnum.EMAIL,
            communicationRole: CommunicationRoleEnum.EH,
        });
        const communicationChannel = get(selector);
        set(selector, {
            ...communicationChannel,
            emailAddress: value instanceof DefaultValue || value === null ? '' : value,
        });
    },
});
export const localPartyCountryCallingCodeSelector = selector({
    key: 'local/party/communicationChannelArray/countryCallingCode',
    get: ({ get }) => {
        const communicationChannel = get(localPartyCommunicationChannelSelectorFamily({
            communicationChannelType: CommunicationTypeEnum.TELEPHONE,
            communicationRole: CommunicationRoleEnum.TH,
        }));
        return communicationChannel?.countryCallingCode || null;
    },
    set: ({ get, set }, value) => {
        const selector = localPartyCommunicationChannelSelectorFamily({
            communicationChannelType: CommunicationTypeEnum.TELEPHONE,
            communicationRole: CommunicationRoleEnum.TH,
        });
        const communicationChannel = get(selector);
        set(selector, {
            ...communicationChannel,
            countryCallingCode: value instanceof DefaultValue || value === null ? undefined : value,
        });
    },
});
export const localPartyAreaCodeSelector = selector({
    key: 'local/party/communicationChannelArray/areaCode',
    get: ({ get }) => {
        const communicationChannel = get(localPartyCommunicationChannelSelectorFamily({
            communicationChannelType: CommunicationTypeEnum.TELEPHONE,
            communicationRole: CommunicationRoleEnum.TH,
        }));
        return communicationChannel?.areaCode || null;
    },
    set: ({ get, set }, value) => {
        const selector = localPartyCommunicationChannelSelectorFamily({
            communicationChannelType: CommunicationTypeEnum.TELEPHONE,
            communicationRole: CommunicationRoleEnum.TH,
        });
        const communicationChannel = get(selector);
        set(selector, {
            ...communicationChannel,
            areaCode: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyLocalPhoneNumberSelector = selector({
    key: 'local/party/communicationChannelArray/localPhoneNumber',
    get: ({ get }) => {
        const communicationChannel = get(localPartyCommunicationChannelSelectorFamily({
            communicationChannelType: CommunicationTypeEnum.TELEPHONE,
            communicationRole: CommunicationRoleEnum.TH,
        }));
        return communicationChannel?.localPhoneNumber || null;
    },
    set: ({ get, set }, value) => {
        const selector = localPartyCommunicationChannelSelectorFamily({
            communicationChannelType: CommunicationTypeEnum.TELEPHONE,
            communicationRole: CommunicationRoleEnum.TH,
        });
        const communicationChannel = get(selector);
        set(selector, {
            ...communicationChannel,
            localPhoneNumber: value instanceof DefaultValue ? null : value,
        });
    },
});
export const localPartyPepSanctionStatusSelector = selector({
    key: 'local/party/PepSanctionStatus',
    get: ({ get }) => get(localPartyPepSanctionStatusAtom),
    set: ({ set }, value) => set(localPartyPepSanctionStatusAtom, value || {}),
});
export const localPartyPepSanctionStatusHelper = createFieldSelector(localPartyPepSanctionStatusSelector);
export const localPartyPepSanctionStatusPepStatusSelector = localPartyPepSanctionStatusHelper('pepStatus', { defaultValue: undefined });
export const localPartyPepSanctionStatusSanctionStatusSelector = localPartyPepSanctionStatusHelper('sanctionStatus', {
    defaultValue: undefined,
});
